import {
    Avatar, Box, Divider, Drawer, Grid, IconButton, List, ListItem,
    ListItemIcon, ListItemText, Menu, MenuItem, Tooltip, Typography, useMediaQuery
} from '@mui/material'
import globalStyle from "../../Styles/ComponentStyles/global.module.scss"
import GlobalButton from '../../Utils/GlobalUtilsComponents/GlobalButton'
import IconsColor from '../../Utils/IconsColor'
import React, { useContext, useState } from 'react'
import { UserContext, UserContextPayload } from '../../Context/UserDetailContext'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useSnackbar } from '../GlobalSnackbar/GlobalSnackbar'
import { ProfileContext, ProfileContextPayload } from '../../Context/ProfileDetailsContext'
import newRBLogoForWhiteBg from '../../assets/Images/mar-lockup.svg'
import newRBLogo from '../../assets/Images/mar-lockup-white.svg'
import { getMenuItems } from '../../Utils/HelperFunctions'
import MenuOutlinedIcon from '@mui/icons-material/MenuOutlined';

interface MenuItemType {
    name: string;
    icon: React.ReactNode;
    selectedIcon?: React.ReactNode;
    path: string;
    subItems?: MenuItemType[];
}

// --- Extracted sub-components to reduce cognitive complexity (S3776) ---

interface NavbarHeaderProps {
    isBelow768: boolean;
    toggleDrawer: (open: boolean) => () => void;
    navbarText?: string;
    navbarBtnText?: string;
    navbarBtnOnClick?: () => void;
    flagSaveCancelBtn?: boolean;
    primaryBtnClick?: () => void;
    secondaryBtnClick?: () => void;
    iseEditMode?: boolean;
    flagSampleProfile?: boolean;
    saveAsSampleProfileClick?: () => void;
    userRole: string;
}

const NavbarHeader: React.FC<NavbarHeaderProps> = ({
    isBelow768, toggleDrawer, navbarText, navbarBtnText, navbarBtnOnClick,
    flagSaveCancelBtn, primaryBtnClick, secondaryBtnClick, iseEditMode,
    flagSampleProfile, saveAsSampleProfileClick, userRole
}) => (
    <Grid item xs={12}>
        <Grid container>
            <Grid item xs={6}>
                <Grid container alignItems={"center"} gap={"20px"}>
                    {isBelow768 &&
                        <Grid item onClick={toggleDrawer(true)}>
                            <MenuOutlinedIcon sx={{ color: "#04385D", marginTop: "5px" }} />
                        </Grid>
                    }
                    <Grid item>
                        <Grid container alignItems="center" spacing={1}>
                            <Grid item>
                                <Typography component='span' className={`${globalStyle.navMainHeadStyle}`}>
                                    {navbarText ?? ""}
                                </Typography>
                            </Grid>
                            {navbarText?.toLowerCase() === "training" && (
                                <Grid item>
                                    <span className={globalStyle.comingSoonSignStyle}>
                                        Coming Soon
                                    </span>
                                </Grid>
                            )}
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
            <Grid item xs={6}>
                <Grid container sx={{ justifyContent: 'end' }}>
                    {navbarBtnText &&
                        <Grid item className={`${globalStyle.globalButtonWrapper}`}>
                            <GlobalButton
                                value={navbarBtnText}
                                size='large'
                                fullWidth={true}
                                variant='contained'
                                className={`${globalStyle.globalButtonStyleV1}`}
                                onClick={navbarBtnOnClick}
                                sx={{ width: "100%" }}
                                startIcon={<IconsColor iconName='plusIcon' />}
                            />
                        </Grid>
                    }
                    {flagSaveCancelBtn &&
                        <SaveCancelButtons
                            primaryBtnClick={primaryBtnClick}
                            secondaryBtnClick={secondaryBtnClick}
                            iseEditMode={iseEditMode}
                            flagSampleProfile={flagSampleProfile}
                            saveAsSampleProfileClick={saveAsSampleProfileClick}
                            userRole={userRole}
                        />
                    }
                </Grid>
            </Grid>
        </Grid>
    </Grid>
)

interface SaveCancelButtonsProps {
    primaryBtnClick?: () => void;
    secondaryBtnClick?: () => void;
    iseEditMode?: boolean;
    flagSampleProfile?: boolean;
    saveAsSampleProfileClick?: () => void;
    userRole: string;
}

const SaveCancelButtons: React.FC<SaveCancelButtonsProps> = ({
    primaryBtnClick, secondaryBtnClick, iseEditMode,
    flagSampleProfile, saveAsSampleProfileClick, userRole
}) => (
    <Grid item>
        <Grid container className={globalStyle.btnContainer}>
            <Grid item>
                <GlobalButton
                    value='Cancel'
                    size='medium'
                    className={`${globalStyle.navCancelBtn}`}
                    onClick={secondaryBtnClick}
                />
            </Grid>
            {flagSampleProfile ? (
                <>
                    {userRole?.toLowerCase() === "admin" &&
                        <Grid item>
                            <GlobalButton
                                value='Update Sample Profile'
                                size='medium'
                                className={`${globalStyle.navUpdateSampleProfileBtn}`}
                                onClick={primaryBtnClick}
                            />
                        </Grid>
                    }
                    <Grid item>
                        <GlobalButton
                            value='Save Profle'
                            size='medium'
                            className={`${globalStyle.navSaveProfileBtn}`}
                            onClick={saveAsSampleProfileClick}
                        />
                    </Grid>
                </>
            ) : (
                <Grid item>
                    <GlobalButton
                        value={iseEditMode ? "Update" : "Create"}
                        size='medium'
                        className={`${globalStyle.navSaveBtn}`}
                        onClick={primaryBtnClick}
                    />
                </Grid>
            )}
        </Grid>
    </Grid>
)

interface TokenHeaderProps {
    profileName: string;
    profileType: string;
}

const TokenHeader: React.FC<TokenHeaderProps> = ({ profileName, profileType }) => (
    <Grid item xs={12}>
        <Grid container alignItems={"center"} justifyContent={"space-between"}>
            <Grid item>
                <p style={{ color: "#0F172B", fontSize: "20px", fontWeight: 600 }}>{profileName}</p>
                <p style={{ fontWeight: 500, marginTop: "2px" }}>Profile Type: {profileType}</p>
            </Grid>
            <Box
                component="img"
                alt="Logo"
                src={newRBLogoForWhiteBg}
                height={"30px"}
            />
        </Grid>
    </Grid>
)

// --- Main component ---

const GlobalNavbar = (props: any) => {

    const isBelow768 = useMediaQuery('(max-width:768px)')
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [profileAnchorEl, setProfileAnchorEl] = useState<any>(null)

    const { showSnackbar } = useSnackbar();

    const {
        userDetail,
        setFlagLoader,
        Logout,
        setUserDetail,
    } = useContext(UserContext) as UserContextPayload

    const {
        profileName,
        profileType
    } = useContext(ProfileContext) as ProfileContextPayload

    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const userToken = searchParams.get('token');

    const {
        navbarBtnText, navbarBtnOnClick, navbarText, flagSaveCancelBtn,
        primaryBtnClick, secondaryBtnClick, iseEditMode, flagSampleProfile,
        saveAsSampleProfileClick, flagBorderBottom
    } = props
    const userRole = userDetail?.user_data?.user_role_type || '';

    const toggleDrawer = (open: boolean) => () => {
        setDrawerOpen(open);
    };

    const handleMenuClick = (path: string) => {
        navigate(path);
        setDrawerOpen(false);
    };

    const handleProfileClick = (e: any) => {
        setProfileAnchorEl(e.currentTarget)
    }

    const handleProfileClose = () => {
        setProfileAnchorEl(null);
    };

    const LogoutUser = async () => {
        setFlagLoader(true)
        const response = await Logout()
        if (response.status) {
            navigate('/login')
            setFlagLoader(false)
            setUserDetail({
                ...userDetail,
                flagLoggedIn: false
            });
        }
        else {
            const errorMessage = response?.error?.message || "Something Went Wrong!"
            showSnackbar(errorMessage, 'error');
            setFlagLoader(false)
            setUserDetail({
                ...userDetail,
                flagLoggedIn: true
            });
        }
    }

    return (
        <>
            <Grid container className={`${globalStyle.navMainWrapper}`} style={{ borderBottom: flagBorderBottom ? "1px solid #E2E8F0" : "none" }}>
                <Grid item xs={12} padding={"20px"}>
                    {/* S7735 fix: flipped negated condition to positive-first */}
                    <Grid container sx={{ gap: userToken ? "0px" : "15px" }}>
                        {userToken ? (
                            <TokenHeader profileName={profileName} profileType={profileType} />
                        ) : (
                            <NavbarHeader
                                isBelow768={isBelow768}
                                toggleDrawer={toggleDrawer}
                                navbarText={navbarText}
                                navbarBtnText={navbarBtnText}
                                navbarBtnOnClick={navbarBtnOnClick}
                                flagSaveCancelBtn={flagSaveCancelBtn}
                                primaryBtnClick={primaryBtnClick}
                                secondaryBtnClick={secondaryBtnClick}
                                iseEditMode={iseEditMode}
                                flagSampleProfile={flagSampleProfile}
                                saveAsSampleProfileClick={saveAsSampleProfileClick}
                                userRole={userRole}
                            />
                        )}
                    </Grid>
                </Grid>
            </Grid>
            {!userToken &&
                <Drawer
                    anchor="left"
                    open={drawerOpen}
                    onClose={toggleDrawer(false)}
                    sx={{
                        "& .MuiDrawer-paper": {
                            backgroundColor: "rgba(97, 97, 97, 0.65) !important"
                        },
                        backgroundColor: "rgba(97, 97, 97, 0.65) !important"
                    }}
                >
                    <Grid container sx={{ width: 250 }} direction={"column"} justifyContent={"space-between"} height={"100vh"}>
                        <Grid item>
                            <Grid container>
                                <Grid item xs={12} padding={"20px 10px 0px 15px"}>
                                    <img src={newRBLogo} alt={'company logo'}></img>
                                </Grid>
                                <Divider
                                    style={{
                                        opacity: 0.8,
                                        borderColor: "#D1D1D1",
                                        height: "1px",
                                        width: "100%",
                                        margin: "10px auto"
                                    }}
                                />
                                <Grid item xs={12}>
                                    <List>
                                        {getMenuItems(userRole).map((item: MenuItemType) => (
                                            <React.Fragment key={item.path}>
                                                <Grid container alignItems={"center"}>
                                                    <Grid item xs={12}>
                                                        <ListItem
                                                            onClick={() => handleMenuClick(item.path)}
                                                            className={`${globalStyle.listItemText}`}
                                                            sx={{
                                                                padding: "0px 15px 0px 15px",
                                                                marginBottom: "10px",
                                                                /* S7764 fix: use globalThis instead of window */
                                                                borderLeft: globalThis.location.pathname.toLowerCase().startsWith(item.path.toLowerCase()) ? "5px solid rgba(16, 94, 167, 1)" : "",
                                                            }}
                                                        >
                                                            <Grid container
                                                                alignItems="center"
                                                                sx={{
                                                                    backgroundColor: globalThis.location.pathname.toLowerCase().startsWith(item.path.toLowerCase()) ? "rgba(16, 94, 167, 1)" : "transparent",
                                                                    borderRadius: "6px",
                                                                    padding: "13px",
                                                                    verticalAlign: "middle"
                                                                }}
                                                            >
                                                                <Grid item display={"flex"}>
                                                                    <ListItemIcon className={`${globalStyle.listItemIcon}`}>
                                                                        {item.icon}
                                                                    </ListItemIcon>
                                                                </Grid>
                                                                {drawerOpen &&
                                                                    <Grid item mt={"2px"}>
                                                                        <ListItemText>
                                                                            {item.name}
                                                                        </ListItemText>
                                                                    </Grid>
                                                                }
                                                            </Grid>
                                                        </ListItem>
                                                    </Grid>
                                                </Grid>
                                            </React.Fragment>
                                        ))}
                                    </List>
                                </Grid>
                            </Grid>
                        </Grid>
                        <Grid item>
                            <Grid container>
                                <Divider
                                    style={{
                                        opacity: 0.8,
                                        borderColor: "#D1D1D1",
                                        height: "1px",
                                        width: "100%",
                                        margin: "20px auto"
                                    }}
                                />
                                <Grid item className={`${globalStyle.userInfo}`}>
                                    <Grid container gap={"23px"} flexWrap={"nowrap"} alignItems={"center"} width={"100%"}>
                                        <Grid item>
                                            <Grid container flexWrap={"nowrap"} alignItems={"center"} gap={"10px"}>
                                                <Grid item>
                                                    <Avatar
                                                        src={userDetail?.user_data?.user_profile_image || ''}
                                                        onClick={(e: any) => {
                                                            if (drawerOpen) {
                                                                handleProfileClick(e);
                                                            }
                                                        }}
                                                        sx={{ borderRadius: "50%" }}
                                                    />
                                                </Grid>
                                                <Grid item>
                                                    <Grid container className={`${globalStyle.userDetails}`}>
                                                        <Tooltip title={userDetail?.user_data?.user_name || 'Guest'}>
                                                            <Grid item xs={12} className={`${globalStyle.userName}`}>
                                                                {userDetail?.user_data?.user_name || 'Guest'}
                                                            </Grid>
                                                        </Tooltip>
                                                        <Grid item xs={12} className={`${globalStyle.userRole}`}>
                                                            {userDetail?.user_data?.user_role_type || ''}
                                                        </Grid>
                                                    </Grid>
                                                </Grid>
                                                <Grid item>
                                                    <IconButton style={{ backgroundColor: "none !important" }} onClick={handleProfileClick}>
                                                        <IconsColor iconName="sideArrow" color="#fff" />
                                                    </IconButton>
                                                    <Menu
                                                        anchorEl={profileAnchorEl}
                                                        open={Boolean(profileAnchorEl)}
                                                        onClose={handleProfileClose}
                                                        transformOrigin={{ horizontal: 'left', vertical: 'center' }}
                                                        anchorOrigin={{ horizontal: 'right', vertical: 'top' }}
                                                    >
                                                        <MenuItem>
                                                            <Grid container flexWrap={"nowrap"} alignItems={"center"} gap={6} width={"250px"} justifyContent={"space-between"} padding={"8px"}>
                                                                <Grid item>
                                                                    <Grid container flexWrap={"nowrap"} alignItems={"center"} gap={1} width={"auto"}>
                                                                        <Grid item>
                                                                            <Avatar
                                                                                src={userDetail?.user_data?.user_profile_image || ''}
                                                                                style={{ borderRadius: "50%" }}
                                                                            />
                                                                        </Grid>
                                                                        <Grid item>
                                                                            <Grid container direction={"column"}>
                                                                                <Grid item className={globalStyle.menuItemTextStyle}>
                                                                                    {userDetail?.user_data?.user_name || 'Guest'}
                                                                                </Grid>
                                                                                <Grid item className={globalStyle.menuItemSubTextStyle}>
                                                                                    {userDetail?.user_data?.user_role_type || ''}
                                                                                </Grid>
                                                                            </Grid>
                                                                        </Grid>
                                                                    </Grid>
                                                                </Grid>
                                                                <Grid item onClick={() => { navigate("/myAccount"); handleProfileClose() }}>
                                                                    <IconsColor iconName="editIcon" backgroundColor="#D9D9D9" />
                                                                </Grid>
                                                            </Grid>
                                                        </MenuItem>

                                                        <Divider
                                                            style={{
                                                                opacity: 0.8,
                                                                borderColor: "#D1D1D1",
                                                                height: "1px",
                                                                width: "90%",
                                                                margin: "0 auto"
                                                            }}
                                                        />

                                                        <MenuItem onClick={() => { navigate("/changePassword"); handleProfileClose() }}>
                                                            <span className={globalStyle.menuItemTextStyle}>Change Password</span>
                                                        </MenuItem>
                                                        <MenuItem onClick={() => { LogoutUser() }}>
                                                            <span className={globalStyle.menuItemTextStyle}>Logout</span>
                                                        </MenuItem>
                                                    </Menu>
                                                </Grid>
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                </Drawer>
            }
        </>
    )
}

export default GlobalNavbar